import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { isUsbTransferFailureReport } from "@utils/webusb-utils/transportTeardown";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

const dsn = process.env.REACT_APP_SENTRY_DSN;
const environment = process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV;
const isProduction = process.env.NODE_ENV === "production";
const forceEnable = process.env.REACT_APP_SENTRY_FORCE_ENABLE === "true";
const release = process.env.REACT_APP_SENTRY_RELEASE;

function parseSampleRate(value: string | undefined, defaultValue: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : defaultValue;
}

const tracesSampleRate = parseSampleRate(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE, 0.1);
const replaysSessionSampleRate = parseSampleRate(
  process.env.REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
  0.05,
);
const replaysOnErrorSampleRate = parseSampleRate(
  process.env.REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
  1.0,
);

// The /print/* route is a synthetic render driven by the backend's headless
// Chromium — not a user session. Any reports from it would be noise (React
// warnings, broken charts re-rendering with NaN before data lands, etc.) and
// consume Sentry quota, so disable instrumentation entirely there.
const isHeadlessPrintRoute =
  typeof window !== "undefined" && window.location.pathname.startsWith("/print/");

Sentry.init({
  dsn,
  enabled:
    (isProduction || forceEnable) &&
    Boolean(dsn) &&
    process.env.REACT_APP_SENTRY_ENABLED !== "false" &&
    !isHeadlessPrintRoute,
  environment,
  sendDefaultPii: false,
  debug: process.env.REACT_APP_SENTRY_DEBUG === "true",
  tracesSampleRate,
  replaysSessionSampleRate,
  replaysOnErrorSampleRate,
  ignoreErrors: [
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications",
    "ChunkLoadError",
  ],
  integrations: [
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],

  tracePropagationTargets: ["localhost", /^https:\/\/([a-z0-9-]+\.)?oculera\.health/],
  beforeSend(event) {
    const exception = event.exception?.values?.[0];
    const message = exception?.value ?? "";
    // Filter out third-party Chatbase errors (ad blockers, network issues)
    if (message.includes("chatbase.co")) {
      return null;
    }
    if (exception?.mechanism?.handled === false && isUsbTransferFailureReport(message)) {
      return null;
    }
    return event;
  },
  ...(release ? { release } : {}),
});
